import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Email,
  Add,
  Delete,
  Send,
  CheckCircle,
  Error as ErrorIcon,
  Settings,
  Notifications,
  History,
  PlayArrow,
  Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const AlertasConfig = () => {
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Email Configuration
  const [emailConfig, setEmailConfig] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    from: ''
  });
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Alert Configuration
  const [registryConfig, setRegistryConfig] = useState({
    months_before_5: true,
    months_before_3: true,
    months_before_1: true,
    days_before_15: true,
    alert_on_expired: true,
    recipient_emails: [],
    enabled: true
  });

  const [documentConfig, setDocumentConfig] = useState({
    months_before_5: true,
    months_before_3: true,
    months_before_1: true,
    days_before_15: true,
    alert_on_expired: true,
    recipient_emails: [],
    enabled: true
  });

  // Email management
  const [newEmail, setNewEmail] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [currentConfigType, setCurrentConfigType] = useState('registry');

  // Logs
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadEmailConfig();
    loadAlertConfigs();
    loadStats();
    loadLogs();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/email/config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.configured && data.config) {
          setEmailConfig({
            ...emailConfig,
            ...data.config
          });
          setEmailConfigured(true);
        }
      }
    } catch (error) {
      console.error('Error cargando configuración de email:', error);
    }
  };

  const loadAlertConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const configs = await response.json();
        configs.forEach(config => {
          if (config.alert_type === 'registry') {
            setRegistryConfig({
              months_before_5: config.months_before_5 === 1,
              months_before_3: config.months_before_3 === 1,
              months_before_1: config.months_before_1 === 1,
              days_before_15: config.days_before_15 === 1,
              alert_on_expired: config.alert_on_expired === 1,
              recipient_emails: config.recipient_emails || [],
              enabled: config.enabled === 1
            });
          } else if (config.alert_type === 'document') {
            setDocumentConfig({
              months_before_5: config.months_before_5 === 1,
              months_before_3: config.months_before_3 === 1,
              months_before_1: config.months_before_1 === 1,
              days_before_15: config.days_before_15 === 1,
              alert_on_expired: config.alert_on_expired === 1,
              recipient_emails: config.recipient_emails || [],
              enabled: config.enabled === 1
            });
          }
        });
      }
    } catch (error) {
      console.error('Error cargando configuraciones de alertas:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/logs?limit=20', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error cargando logs:', error);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const handleSaveEmailConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/email/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailConfig)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Configuración de email guardada exitosamente');
        setEmailConfigured(true);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error guardando configuración');
      }
    } catch (error) {
      setError('Error guardando configuración de email');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/email/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('✅ Configuración de email verificada correctamente');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error verificando configuración');
      }
    } catch (error) {
      setError('Error verificando configuración de email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!emailConfig.user) {
      setError('Primero configura el email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/email/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ to: emailConfig.user })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('📧 Email de prueba enviado correctamente');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error enviando email de prueba');
      }
    } catch (error) {
      setError('Error enviando email de prueba');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAlertConfig = async (type) => {
    setLoading(true);
    setError('');
    setSuccess('');

    const config = type === 'registry' ? registryConfig : documentConfig;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          alert_type: type,
          ...config
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Configuración de alertas de ${type === 'registry' ? 'registros' : 'documentos'} guardada`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Error guardando configuración');
      }
    } catch (error) {
      setError('Error guardando configuración de alertas');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail) return;

    // Validación básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Email inválido');
      return;
    }

    const config = currentConfigType === 'registry' ? registryConfig : documentConfig;

    if (config.recipient_emails.includes(newEmail)) {
      setError('Este email ya está en la lista');
      return;
    }

    const newEmails = [...config.recipient_emails, newEmail];

    if (currentConfigType === 'registry') {
      setRegistryConfig({ ...registryConfig, recipient_emails: newEmails });
    } else {
      setDocumentConfig({ ...documentConfig, recipient_emails: newEmails });
    }

    setNewEmail('');
    setEmailDialogOpen(false);
    setError('');
  };

  const handleRemoveEmail = (type, email) => {
    if (type === 'registry') {
      setRegistryConfig({
        ...registryConfig,
        recipient_emails: registryConfig.recipient_emails.filter(e => e !== email)
      });
    } else {
      setDocumentConfig({
        ...documentConfig,
        recipient_emails: documentConfig.recipient_emails.filter(e => e !== email)
      });
    }
  };

  const handleRunCheckNow = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/alerts/check-now', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`✅ Verificación completada: ${data.result.alertsSent} alertas enviadas`);
        loadLogs();
        loadStats();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Error ejecutando verificación');
      }
    } catch (error) {
      setError('Error ejecutando verificación de vencimientos');
    } finally {
      setLoading(false);
    }
  };

  const renderAlertConfig = (type, config, setConfig) => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Alertas de {type === 'registry' ? 'Registros Sanitarios' : 'Documentos'}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                color="primary"
              />
            }
            label={config.enabled ? 'Activado' : 'Desactivado'}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Niveles de Alerta
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={config.months_before_5}
                  onChange={(e) => setConfig({ ...config, months_before_5: e.target.checked })}
                />
              }
              label="5 meses antes del vencimiento"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.months_before_3}
                  onChange={(e) => setConfig({ ...config, months_before_3: e.target.checked })}
                />
              }
              label="3 meses antes del vencimiento"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.months_before_1}
                  onChange={(e) => setConfig({ ...config, months_before_1: e.target.checked })}
                />
              }
              label="1 mes antes del vencimiento"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.days_before_15}
                  onChange={(e) => setConfig({ ...config, days_before_15: e.target.checked })}
                />
              }
              label="15 días antes del vencimiento"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.alert_on_expired}
                  onChange={(e) => setConfig({ ...config, alert_on_expired: e.target.checked })}
                />
              }
              label="Cuando ya está vencido"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Destinatarios ({config.recipient_emails.length})
              </Typography>
              <Button
                startIcon={<Add />}
                variant="outlined"
                size="small"
                onClick={() => {
                  setCurrentConfigType(type);
                  setEmailDialogOpen(true);
                }}
              >
                Agregar Email
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {config.recipient_emails.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay destinatarios configurados
                </Typography>
              ) : (
                config.recipient_emails.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    onDelete={() => handleRemoveEmail(type, email)}
                    color="primary"
                    variant="outlined"
                  />
                ))
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => handleSaveAlertConfig(type)}
              disabled={loading}
              fullWidth
            >
              Guardar Configuración
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          ⚙️ Configuración de Alertas
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<PlayArrow />}
          onClick={handleRunCheckNow}
          disabled={loading || !emailConfigured}
        >
          Ejecutar Verificación Ahora
        </Button>
      </Box>

      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Alert
              severity={error ? 'error' : 'success'}
              onClose={() => error ? setError('') : setSuccess('')}
              sx={{ mb: 3 }}
            >
              {error || success}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab icon={<Email />} label="Configuración Email" />
        <Tab icon={<Notifications />} label="Alertas" />
        <Tab icon={<History />} label="Historial" />
      </Tabs>

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Configuración del Servidor SMTP
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configura el servidor de correo para enviar las alertas
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Servidor SMTP (Host)"
                  value={emailConfig.host}
                  onChange={(e) => setEmailConfig({ ...emailConfig, host: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Puerto"
                  type="number"
                  value={emailConfig.port}
                  onChange={(e) => setEmailConfig({ ...emailConfig, port: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Usuario / Email"
                  value={emailConfig.user}
                  onChange={(e) => setEmailConfig({ ...emailConfig, user: e.target.value })}
                  placeholder="tu-email@gmail.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="password"
                  value={emailConfig.pass}
                  onChange={(e) => setEmailConfig({ ...emailConfig, pass: e.target.value })}
                  placeholder="Contraseña o App Password"
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Remitente (From)"
                  value={emailConfig.from}
                  onChange={(e) => setEmailConfig({ ...emailConfig, from: e.target.value })}
                  placeholder="Sistema de Alertas <alertas@empresa.com>"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailConfig.secure}
                      onChange={(e) => setEmailConfig({ ...emailConfig, secure: e.target.checked })}
                    />
                  }
                  label="SSL/TLS"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveEmailConfig}
                    disabled={loading}
                    fullWidth
                  >
                    Guardar Configuración
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleVerifyEmail}
                    disabled={loading || !emailConfigured}
                    fullWidth
                    startIcon={<CheckCircle />}
                  >
                    Verificar
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSendTestEmail}
                    disabled={loading || !emailConfigured}
                    fullWidth
                    startIcon={<Send />}
                  >
                    Enviar Prueba
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Box>
          {renderAlertConfig('registry', registryConfig, setRegistryConfig)}
          {renderAlertConfig('document', documentConfig, setDocumentConfig)}
        </Box>
      )}

      {tabValue === 2 && (
        <Box>
          {stats && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="success.main">{stats.totalSent}</Typography>
                    <Typography variant="body2" color="text.secondary">Enviadas</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="error.main">{stats.totalFailed}</Typography>
                    <Typography variant="body2" color="text.secondary">Fallidas</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="primary.main">{stats.registryAlerts}</Typography>
                    <Typography variant="body2" color="text.secondary">Registros</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="secondary.main">{stats.documentAlerts}</Typography>
                    <Typography variant="body2" color="text.secondary">Documentos</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Últimas Alertas Enviadas
                </Typography>
                <IconButton onClick={loadLogs}>
                  <Refresh />
                </IconButton>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Nivel</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{new Date(log.sent_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.alert_type === 'registry' ? 'Registro' : 'Documento'}
                            size="small"
                            color={log.alert_type === 'registry' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>{log.record_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.alert_level}
                            size="small"
                            color={
                              log.alert_level === 'expired' ? 'error' :
                              log.alert_level === '15_days' ? 'warning' : 'info'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {log.status === 'sent' ? (
                            <CheckCircle color="success" />
                          ) : (
                            <ErrorIcon color="error" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Dialog para agregar email */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
        <DialogTitle>Agregar Email de Destinatario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="ejemplo@correo.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddEmail} variant="contained">
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertasConfig;
